// Build Line - one order, in full.
//
// The station board is the point of this screen. An order is not a progress bar:
// it is eleven stations across six phases, three of which hand work to each
// other MID-PHASE (rename starts while DKIM is still minting, a panel's IMAPs
// push goes the moment that panel's renames land, the sequencer streams per
// mailbox behind login proof), so "which station is moving and which is stuck"
// is the only useful question and a single percentage cannot answer it.
//
// FOUR THINGS THIS SCREEN REFUSES TO DO:
//
//  1. Compute its own gates. Every done / ready / waiting count comes from the
//     server, which evaluates the REAL station predicates over this order's
//     items. A second implementation here would be a second opinion, and the
//     coordinator's is the one that actually moves the build.
//  2. Blur the hard gates into the soft tails. The hard-gate checklist decides
//     `delivered`; the finisher checklist is what the hourly sweep is still
//     chasing behind it. They are two cards, never one list, because
//     "delivered, tails trailing" is a real state an operator must be able to
//     read at a glance.
//  3. Paint a stamp it did not see. A stamp cell is a tick only when the
//     timestamp is non-null; not yet is a dash, and the exact time lives in the
//     tooltip so nine stamp columns still fit in a row.
//  4. Offer an action as harmless. Continue anyway abandons items permanently
//     and cascades a domain's abandonment onto every mailbox on it; Abort ends
//     the order and purges the customer's borrowed credentials. Both say so
//     before they run, and Abort has to be typed out.
//
// Polling is 3 s (the live-detail cadence shared with ForeignDnsRunDetail and
// the Sequencer Connect progress view) and PAUSES WHILE THE TAB IS HIDDEN;
// coming back reads immediately. A finished order stops polling altogether.

import { useCallback, useEffect, useRef, useState } from 'react';

import type { BuildShowToast } from './BuildLine';
import {
  BUILD_PERMANENT_CODES,
  buildApi,
  buildErrorMessage,
  buildIsParked,
  type BuildDomainItem,
  type BuildMailboxItem,
  type BuildOrderDetail,
  type BuildOrderRow,
  type BuildOrderStatus,
  type BuildStampWindow,
  type BuildStationCard,
  type BuildTailResult,
  type DeliveryReport,
  type DeliveryExplanation,
} from './buildApi';
import { BUILD_STATUS_LABEL, Progress, ago, buildStatusTone, buildPhaseTone, fmtDate, useVisiblePoll } from './Orders';
import { groupAttention, hasHistoricalQaGap, historicalReadNote, isDelivered } from './presentation';
import { AttentionMeaning, DeliveryMeaning, IssueMeaning } from './DeliveryMeaning';
import { panelSplitLabel } from './panel-split';

/** Same 3 s as every other live detail screen in the app. */
const POLL_MS = 3000;

/** The elapsed clock ticks once a second so it reads as a clock. It is derived
 *  from timestamps, never counted, so a paused tab resumes at the right value. */
const CLOCK_MS = 1000;

/** Nothing moves an order in one of these again - not the coordinator, not the
 *  finisher sweep, not an action on this page. Polling stops; Refresh stays. */
const TERMINAL: ReadonlySet<BuildOrderStatus> = new Set<BuildOrderStatus>(['closed', 'aborted']);

/** What each station does, in the operator's words. The coordinator names them;
 *  this only translates. A station missing from the map renders its raw name,
 *  which is what a station added server-side and not mirrored here should do. */
const STATION_LABEL: Record<string, { label: string; what: string }> = {
  allocate: { label: 'Allocate', what: 'pin each domain to a panel with free seats' },
  claim: { label: 'Claim', what: 'create the holding users and licence them' },
  zones: { label: 'Zones', what: 'DNS hosting per registrable root is accessible and ready' },
  dns_records: { label: 'DNS records', what: 'SPF, DKIM placeholder, DMARC, MX and tracking' },
  forwarding: { label: 'Forwarding', what: 'redirect rule to the order target url' },
  domain_add: { label: 'Domain add', what: 'add and verify domains inside Google; trust the required apps' },
  dkim: { label: 'DKIM', what: 'publish the expected signing key and activate DKIM inside Google' },
  rename: { label: 'Rename', what: 'holding address to the final address, aliases stripped' },
  photos: { label: 'Photos', what: 'profile photo per mailbox' },
  imaps: { label: 'IMAPs push', what: 'push to the orchestrator and wait for login proof' },
  sequencer: { label: 'Sequencer', what: 'connect the mailboxes and enable warmup' },
  masterinbox: { label: 'MasterInbox', what: 'one channel per mailbox in the customer workspace' },
};

/**
 * The stamps each station writes, for the per-station elapsed on its card.
 *
 * The server hands back a {first, last, count} window per stamp across the whole
 * order (`timings.stamps`, built by stampWindows precisely so durations are
 * derivable), and a station's span is the earliest first to the latest last
 * across the stamps it owns. Several stations write more than one - claim stamps
 * the seat and then its licence, domain_add stamps added, verified and DKIM
 * inside one browser session - and taking only the finish line would report a
 * two-hour station as an instant.
 *
 * `allocate` is absent deliberately: it writes `panel_email`, not a timestamp,
 * so there is no window to report and the card shows none rather than a zero.
 * `tracking_active_at` is absent too - it belongs to the finisher sweep, which
 * is the soft-tails card, not a station.
 */
const STATION_STAMPS: Record<string, string[]> = {
  claim: ['claimed_at', 'licensed_at'],
  zones: ['zone_active_at'],
  dns_records: ['records_at'],
  forwarding: ['forwarding_at'],
  domain_add: ['added_at', 'verified_at'],
  dkim: ['dkim_active_at'],
  rename: ['renamed_at'],
  photos: ['photo_at'],
  imaps: ['imaps_pushed_at', 'login_proof_at'],
  sequencer: ['seq_connected_at', 'warmup_enabled_at'],
  masterinbox: ['mi_connected_at'],
};

/**
 * Which item table a station's parks live in.
 *
 * Used ONLY to count parked items beside a station, and the count is an UPPER
 * BOUND rather than an exact figure - see `parkedFor`, which clamps it. The
 * useful half is true: every station's gate refuses a parked item whatever
 * parked it, so a parked item this station has not finished is one its gate is
 * skipping. What this map cannot tell is whether a given parked item is one this
 * station already finished with, and the server puts those in `done`.
 *
 * `domain_add` also owns the PANEL rows: the browser-queue wrapper stamps a
 * failed job on the panel, not on the domains it was carrying.
 */
const STATION_ITEMS: Record<string, { domains?: boolean; mailboxes?: boolean; panels?: boolean }> = {
  allocate: { domains: true },
  claim: { mailboxes: true },
  zones: { domains: true },
  dns_records: { domains: true },
  forwarding: { domains: true },
  domain_add: { domains: true, panels: true },
  dkim: { domains: true, panels: true },
  rename: { mailboxes: true },
  photos: { mailboxes: true },
  imaps: { mailboxes: true },
  sequencer: { mailboxes: true },
  masterinbox: { mailboxes: true },
};

const PHASE_LABEL: Record<string, string> = {
  preflight: 'P1 preflight',
  provision: 'P2 provision',
  dns: 'P3 dns',
  build: 'P4 build',
  connect: 'P5 connect',
  deliver: 'P6 deliver',
};

const DOMAIN_STAMPS: { key: keyof BuildDomainItem; label: string }[] = [
  { key: 'zone_active_at', label: 'zone active' },
  { key: 'records_at', label: 'records written' },
  { key: 'added_at', label: 'added to Google' },
  { key: 'verified_at', label: 'verified' },
  { key: 'dkim_active_at', label: 'DKIM active' },
  { key: 'forwarding_at', label: 'forwarding written' },
  { key: 'tracking_active_at', label: 'tracking active' },
];

const MAILBOX_STAMPS: { key: keyof BuildMailboxItem; label: string }[] = [
  { key: 'claimed_at', label: 'claimed' },
  { key: 'licensed_at', label: 'licensed' },
  { key: 'renamed_at', label: 'renamed' },
  { key: 'photo_at', label: 'photo' },
  { key: 'imaps_pushed_at', label: 'IMAPs pushed' },
  { key: 'login_proof_at', label: 'login proved' },
  { key: 'seq_connected_at', label: 'sequencer connected' },
  { key: 'warmup_enabled_at', label: 'warmup enabled' },
  { key: 'mi_connected_at', label: 'MasterInbox connected' },
];

type ItemTab = 'domains' | 'mailboxes' | 'panels';
/** `qa` is the delivery report re-run: it queues a station job and changes no
 *  item, so like retry it needs no confirm. `release` / `release-stop` arm and
 *  stop the hourly retry of held domains - reversible, so no confirm either. */
type OrderAction = 'retry' | 'continue' | 'abort' | 'qa' | 'release' | 'release-stop';

/** Every action that changes the order needs a confirm. Retry is not here: it
 *  is the reversible one, and a modal in front of the button an operator presses
 *  most would train them to click through modals. */
const CONFIRM: Record<'continue' | 'abort', { title: string; body: string; cta: string }> = {
  continue: {
    title: 'Continue without the parked items?',
    body:
      'Every parked item is marked abandoned and the order is delivered without it. Abandoned items '
      + 'are never retried and drop out of every gate and every ratio, so the order can reach '
      + 'delivered while they sit unbuilt. Abandoning a DOMAIN cascades onto every mailbox on it, '
      + 'whether or not those mailboxes ever failed. Retry failed is the reversible option.',
    cta: 'Abandon them and continue',
  },
  abort: {
    title: 'Abort this order?',
    body:
      'The order ends here. Nothing already built is torn down - the zones, the domains, the '
      + 'mailboxes and the sequencer connections all stay exactly as they are - but no station will '
      + "touch this order again. The customer's borrowed credentials are purged and the Sequencer "
      + 'Connect run is archived, so resuming means enrolling a new order with the keys re-supplied. '
      + 'This cannot be undone.',
    cta: 'Abort the order',
  },
};

/** The word an abort has to be typed out as. Same shape of gate as the typed
 *  DELETE on domain removal: a destructive button a mis-click cannot reach. */
const ABORT_WORD = 'ABORT';

/** The event history keeps detail strings up to this length (server
 *  EVENT_DETAIL_TEXT_MAX); the server refuses longer ones rather than dropping
 *  them, so the box stops the operator at the same point. */
const ABORT_REASON_MAX = 200;

function Stamp({ label, at }: { label: string; at: string | null }): JSX.Element {
  return at ? (
    <span className="bl-stamp" title={`${label}: ${fmtDate(at)}`}>
      {'✓'}
    </span>
  ) : (
    <span className="bl-stamp bl-stamp--off" title={`${label}: not yet`}>
      -
    </span>
  );
}

function MetaItem({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="bl-meta-item">
      <span className="bl-meta-label">{label}</span>
      <span className="bl-meta-value">{value}</span>
    </div>
  );
}

/** h:mm:ss from two instants. Tabular in the CSS, because it is a clock and a
 *  clock whose digits move sideways is unreadable. */
function elapsed(fromIso: string, toMs: number): string {
  const ms = toMs - new Date(fromIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '-';
  const secs = Math.floor(ms / 1000);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * The header's elapsed clock, and the ONLY thing on this screen that re-renders
 * once a second.
 *
 * It owns its own `now` deliberately: a 600-mailbox order's item tables are the
 * rest of this component, and re-reconciling them every second to move one digit
 * is work nobody asked for. It also stops ticking the moment the order finishes,
 * because from then on both ends of the interval are fixed timestamps.
 */
function ElapsedClock({ from, to, live, label }: { from: string; to: string | null; live: boolean; label: string }): JSX.Element {
  const [now, setNow] = useState(() => Date.now());
  const tick = useCallback(() => setNow(Date.now()), []);
  useVisiblePoll(tick, CLOCK_MS, live && to === null);
  return (
    <span className="bl-clock" title={`${fmtDate(from)} → ${to ? fmtDate(to) : 'now'}`}>
      <span className="bl-clock-label">{label}</span> {elapsed(from, to === null ? now : new Date(to).getTime())}
    </span>
  );
}

/** One soft tail's line in the finisher checklist. A tail that is `clear` has
 *  either been proven or was never required, and the state says which. */
function TailRow({ name, tail }: { name: string; tail: BuildTailResult | undefined }): JSX.Element {
  if (!tail) {
    return (
      <div className="bl-gate">
        <div className="bl-gate-head">
          <span className="bl-gate-mark">-</span>
          <span className="bl-gate-name">{name}</span>
        </div>
        <div className="bl-gate-detail">No result was saved for this check.</div>
      </div>
    );
  }
  const notRequired = ['not_required', 'not_applicable', 'no_domains'].includes(tail.state);
  const missingKey = tail.state === 'unverifiable_no_key';
  const partial = tail.state === 'partial';
  const confirmed = tail.clear && !notRequired && !missingKey && !partial;
  const meaning = notRequired ? 'Not required for this order.'
    : missingKey ? 'The borrowed API key was removed, so the current result cannot be checked. No failure was confirmed.'
      : tail.state === 'setup_required' ? 'Tracking setup is waiting for the next automatic attachment check.'
      : tail.state === 'pending' ? name === 'Photo visibility pass'
        ? 'Signing in to each photographed mailbox and setting its picture to "Anyone". The order closes when this finishes.'
        : 'The automatic check is still pending. PI can review the result here when it finishes.'
        : partial ? 'Some pictures could not be set to "Anyone". PI: set those mailboxes by hand; the saved details list each one and why.'
        : tail.state === 'visible' ? 'Every photographed mailbox shows its picture to "Anyone".'
        : tail.state === 'unproven' ? 'The last check could not confirm forwarding. PI: check the destination and redirect before making changes.'
          : tail.state.startsWith('error:') ? 'This check could not finish. PI: inspect the saved error before retrying.'
            : confirmed ? name === 'Tracking domains' ? 'Instantly confirmed active tracking on the expected hostname for every account.' : 'Confirmed by the saved check.' : 'PI: review the saved result to determine whether a correction is needed.';
  return (
    <div className={`bl-gate ${confirmed ? 'bl-gate--ok' : partial || (!tail.clear && tail.state !== 'pending') ? 'bl-gate--warn' : ''}`}>
      <div className="bl-gate-head">
        <span className="bl-gate-mark">{confirmed ? '✓' : '○'}</span>
        <span className="bl-gate-name">{name}</span>
      </div>
      <div className="bl-gate-detail">
        {meaning}
        {name === 'Tracking domains' && tail.detail && <div>
          {typeof tail.detail.pending === 'number' && <span>{tail.detail.pending} domain(s) pending. </span>}
          {tail.detail.hosts && typeof tail.detail.hosts === 'object' ? <span>
            {Object.values(tail.detail.hosts as Record<string, { state?: string }>).filter((h) => h.state === 'dns_pending').length} waiting for DNS;{' '}
            {Object.values(tail.detail.hosts as Record<string, { state?: string }>).filter((h) => h.state === 'ssl_pending').length} waiting for certificates;{' '}
            {Object.values(tail.detail.hosts as Record<string, { state?: string }>).filter((h) => h.state === 'setup_required').length} need certificate setup;{' '}
            {Object.values(tail.detail.hosts as Record<string, { state?: string }>).filter((h) => h.state === 'attachment_pending').length} waiting for mailbox attachment;{' '}
            {Object.values(tail.detail.hosts as Record<string, { state?: string }>).filter((h) => h.state === 'verification_pending').length} waiting for TXT verification.{' '}
          </span> : null}
          {Array.isArray(tail.detail.ssl_unverified_hosts) && tail.detail.ssl_unverified_hosts.length > 0 && <div>
            SSL was not verified for {tail.detail.ssl_unverified_hosts.length} tracking host(s). This diagnostic does not block completion.
          </div>}
          {typeof tail.detail.checked_at === 'string' && <span>Last checked {fmtDate(tail.detail.checked_at)}. </span>}
          {typeof tail.detail.next_check_at === 'string' && <span>Next check {fmtDate(tail.detail.next_check_at)}. </span>}
          {typeof tail.detail.attached === 'number' && <span>{tail.detail.attached} attachment(s) succeeded in this check.</span>}
        </div>}
        {name === 'Photo visibility pass' && tail.detail && typeof tail.detail.visible === 'number' && <div>
          {tail.detail.visible} visible to Anyone
          {typeof tail.detail.pending === 'number' ? `, ${tail.detail.pending} in progress` : ''}
          {typeof tail.detail.failed === 'number' && tail.detail.failed > 0 ? `, ${tail.detail.failed} not set` : ''}.
        </div>}
        <details className="bl-details"><summary>Saved check details</summary>
          <div className="bl-details-body bl-mono">{tail.state}{tail.detail ? ` (${JSON.stringify(tail.detail)})` : ''}</div>
        </details>
      </div>
    </div>
  );
}

/** The sequencer's display name in the delivery report, mirrored from the
 *  station's PROVIDER_NAMES. An unknown provider renders as "Sequencer". */
const PROVIDER_LABEL: Record<string, string> = {
  instantly: 'Instantly', smartlead: 'Smartlead', plusvibe: 'PlusVibe', bison: 'Bison',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The one clock the report speaks: UTC, because the stamps are and the team
 *  reads them from three time zones. `HH:MM`, or `HH:MM:SS` on the chart. */
function utcClock(iso: string, seconds = false): string {
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}${seconds ? `:${pad(d.getUTCSeconds())}` : ''}`;
}

/** `HH:MM`, with the day in front only when `iso` falls on a different UTC day
 *  than `anchor`: a QA re-run a week after delivery, or a build that crossed
 *  midnight, must not read as the same afternoon. */
function utcClockFrom(iso: string, anchor: string | null): string {
  const clock = utcClock(iso);
  const day = (at: string): string => new Date(at).toISOString().slice(0, 10);
  if (anchor === null || day(iso) === day(anchor)) return clock;
  const d = new Date(iso);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${clock}`;
}

/** `50 min 03 s`, or `1 h 05 min 03 s` past the hour. A FIXED duration from
 *  the report, unlike elapsed(): both ends are stamps, so nothing ticks. */
function durationWords(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return h > 0 ? `${h} h ${pad(m)} min ${pad(s)} s` : `${m} min ${pad(s)} s`;
}

/** `+2m`, `+1h 10m`: an offset from launch, for the chart's row titles. */
function offsetWords(ms: number): string {
  const mins = Math.round(ms / 60_000);
  const h = Math.floor(mins / 60);
  return h > 0 ? `+${h}h ${mins % 60}m` : `+${mins}m`;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** `Instantly and MasterInbox`, `Google, IMAPs and Instantly`. */
function listWords(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** One tile of the report. `workspace` is the header's second line, on the
 *  sequencer and MasterInbox tiles only (the operator asked to see the two
 *  names first). `word` is a metric that is a word (Skipped, Not ordered, Not
 *  read) rather than a ratio, and renders muted; `unproven` is a stack whose
 *  oracle could not read its platform - the ledger fallback or a plain read
 *  failure - and renders on honey. */
interface ReportTile {
  name: string;
  workspace: string | null;
  mark: 'ok' | 'warn' | 'bad' | null;
  metric: string;
  word: boolean;
  label: string | null;
  subs: string[];
  note: string | null;
  unproven: boolean;
  historical?: boolean;
}

const TILE_MARK: Record<'ok' | 'warn' | 'bad', string> = { ok: '✓', warn: '!', bad: '✗' };

function markOf(stack: { pass: boolean; read_error?: string }): 'ok' | 'warn' | 'bad' {
  return stack.read_error !== undefined ? 'warn' : stack.pass ? 'ok' : 'bad';
}

/** The header's workspace line: `Advertisable`, `Cold Labs (workspace 838)`,
 *  `Workspace 838`, or the honest `Workspace not reported` when the oracle read
 *  neither. A sequencer id next to its name is a UUID and stays off the tile;
 *  the MasterInbox id is a short integer the operator types into the console,
 *  so it stays. */
function workspaceWords(ws: { id: string | number | null; name: string | null }, withId: boolean): string {
  if (ws.name !== null && ws.id !== null && withId) return `${ws.name} (workspace ${ws.id})`;
  if (ws.name !== null) return ws.name;
  if (ws.id !== null) return `Workspace ${ws.id}`;
  return 'Workspace not reported';
}

/** The four tiles from the four stacks. Denominators are the LIVE counts
 *  (abandoned items drop out of every ratio, see Continue anyway) - the same
 *  numbers the Slack message uses, so the two never disagree. */
function tilesOf(report: DeliveryReport, status: BuildOrderStatus): ReportTile[] {
  const { google, imaps, sequencer, masterinbox } = report.stacks;
  const liveMailboxes = report.totals.mailboxes - report.totals.abandoned_mailboxes;
  const liveDomains = report.totals.domains - report.totals.abandoned_domains;
  const unread = (name: string, reason: string, workspace: string | null = null): ReportTile => ({
    name, workspace, mark: 'warn', metric: 'Not read', word: true, label: null, subs: [],
    note: `live read unavailable: ${reason}`, unproven: true,
  });
  const ledgerNote = (reason: string | undefined): string =>
    `from the build ledger: live read unavailable, ${reason ?? 'unknown'}`;

  const googleTile: ReportTile = google.read_error !== undefined
    ? unread('Google', google.read_error)
    : {
        name: 'Google', workspace: null, mark: markOf(google), metric: `${google.licensed}/${liveMailboxes}`, word: false,
        label: 'mailboxes licensed',
        subs: [
          `${google.domains_verified}/${liveDomains} domains verified`,
          `DKIM ${google.dkim_active}/${liveDomains}, DNS ${google.dns_ok}/${liveDomains}`,
        ],
        note: null, unproven: false,
      };

  const imapsTile: ReportTile = imaps.read_error !== undefined
    ? unread('IMAPs', imaps.read_error)
    : {
        name: 'IMAPs', workspace: null, mark: markOf(imaps), metric: `${imaps.login_proofs}/${liveMailboxes}`, word: false,
        label: 'login proofs', subs: [], note: null, unproven: false,
      };

  const seqName = sequencer.skipped ? 'Sequencer' : PROVIDER_LABEL[sequencer.provider] ?? 'Sequencer';
  const seqWs = workspaceWords(sequencer.workspace, false);
  // The ledger stamp is per DOMAIN, so its denominator is too. Reports stored
  // before `tracking_requested` existed keep the ratio - "not requested" only
  // when the report says so.
  const tracking = sequencer.provider === 'plusvibe' || sequencer.provider === 'bison'
    ? 'custom tracking not used by this integration'
    : sequencer.tracking_requested === false
    ? 'tracking not requested'
    : sequencer.source === 'ledger'
      ? `tracking ${sequencer.tracking_attached}/${liveDomains} domains`
      : `tracking ${sequencer.tracking_attached}/${sequencer.connected}`;
  let seqTile: ReportTile;
  if (sequencer.skipped) {
    seqTile = {
      name: seqName, workspace: null, mark: null, metric: 'Skipped', word: true, label: 'not part of this order',
      subs: [], note: null, unproven: false,
    };
  } else if (sequencer.source === 'ledger') {
    const historical = isDelivered(status) ? historicalReadNote(sequencer.read_error) : null;
    seqTile = {
      name: seqName, workspace: seqWs, mark: historical ? null : 'warn', metric: `${sequencer.connected}/${liveMailboxes}`, word: false,
      label: 'recorded connected', subs: [`${sequencer.warmup_active} recorded warming`, tracking],
      note: historical ?? ledgerNote(sequencer.read_error), unproven: !historical, historical: !!historical,
    };
  } else if (sequencer.read_error !== undefined) {
    seqTile = unread(seqName, sequencer.read_error, seqWs);
  } else {
    seqTile = {
      name: seqName, workspace: seqWs, mark: markOf(sequencer), metric: `${sequencer.connected}/${liveMailboxes}`,
      word: false, label: 'connected',
      subs: [`${sequencer.warmup_active} warming`, `${sequencer.verified} config OK`, tracking],
      note: null, unproven: false,
    };
  }

  const miWs = workspaceWords(masterinbox.workspace, true);
  let miTile: ReportTile;
  if (!masterinbox.enabled) {
    miTile = {
      name: 'MasterInbox', workspace: null, mark: null, metric: 'Not ordered', word: true,
      label: 'not part of this order', subs: [], note: null, unproven: false,
    };
  } else if (masterinbox.source === 'ledger') {
    const historical = isDelivered(status) ? historicalReadNote(masterinbox.read_error) : null;
    miTile = {
      name: 'MasterInbox', workspace: miWs, mark: historical ? null : 'warn', metric: `${masterinbox.healthy}/${liveMailboxes}`,
      word: false, label: 'recorded connected', subs: [], note: historical ?? ledgerNote(masterinbox.read_error),
      unproven: !historical, historical: !!historical,
    };
  } else if (masterinbox.read_error !== undefined) {
    miTile = unread('MasterInbox', masterinbox.read_error, miWs);
  } else {
    miTile = {
      name: 'MasterInbox', workspace: miWs, mark: markOf(masterinbox), metric: `${masterinbox.healthy}/${liveMailboxes}`,
      word: false, label: 'healthy', subs: [], note: null, unproven: false,
    };
  }

  return [googleTile, imapsTile, seqTile, miTile];
}

/** The headline and the one sentence under it. Partial names the stacks that
 *  could not be read live; a closed order says so and that the keys are gone. */
function headlineOf(
  report: DeliveryReport,
  order: Pick<BuildOrderRow, 'status' | 'closed_at'>,
): { verdict: string; tone: 'sage' | 'honey' | 'rose'; sentence: string } {
  const { totals } = report;
  const liveMailboxes = totals.mailboxes - totals.abandoned_mailboxes;
  const liveDomains = totals.domains - totals.abandoned_domains;
  const abandoned = totals.abandoned_mailboxes > 0 || totals.abandoned_domains > 0
    ? ` (${plural(totals.abandoned_mailboxes, 'mailbox', 'mailboxes')} and `
      + `${plural(totals.abandoned_domains, 'domain', 'domains')} abandoned, excluded from every ratio)`
    : '';
  // The turnaround is not in the sentence: it is the stat block beside the
  // headline (ReportTurnaround), so the operator reads it in one glance.
  const sentence = [`${liveMailboxes} mailboxes on ${liveDomains} domains${abandoned}.`];
  sentence.push(`Saved QA check: ${fmtDate(report.generated_at)}.`);

  const historical = hasHistoricalQaGap(report, order.status);
  if (historical) {
    sentence.push('The saved QA report has limited historical coverage. See the affected stack below for the reason.');
  } else if (report.verdict === 'partial') {
    const { google, imaps, sequencer, masterinbox } = report.stacks;
    const unread = [
      google.read_error !== undefined ? 'Google' : null,
      imaps.read_error !== undefined ? 'IMAPs' : null,
      sequencer.read_error !== undefined ? PROVIDER_LABEL[sequencer.provider] ?? 'the sequencer' : null,
      masterinbox.read_error !== undefined ? 'MasterInbox' : null,
    ].filter((n): n is string => n !== null);
    sentence.push(`${listWords(unread)} could not be read live.`);
  }
  if (order.status === 'closed' && order.closed_at) {
    sentence.push(`Closed ${fmtDate(order.closed_at)}.`);
  }
  return {
    verdict: !isDelivered(order.status)
      ? report.verdict === 'all_clear' ? 'Verification complete' : report.verdict === 'issues' ? 'Verification found issues' : 'Verification incomplete'
      : order.status === 'closed' ? 'Order closed' : 'Order delivered',
    tone: report.issues.length > 0 || report.verdict === 'issues' ? 'rose'
      : report.verdict === 'all_clear' || historical ? 'sage' : 'honey',
    sentence: sentence.join(' '),
  };
}

/**
 * The turnaround, launch to delivery, as the one stat beside the headline.
 *
 * The same big-number style as the tiles, because it is the first number the
 * operator looks for on a delivered order. `duration_seconds` is null when the
 * order has no launch stamp; the slot then says so in the tiles' muted word
 * style rather than inventing a figure, and the clock line stays off.
 */
function ReportTurnaround({ timeline }: { timeline: DeliveryReport['timeline'] }): JSX.Element {
  const seconds = timeline.duration_seconds;
  return (
    <div className="bl-report-turnaround">
      <div className={`bl-report-turnaround-value ${seconds === null ? 'bl-report-turnaround-value--word' : ''}`}>
        {seconds === null ? 'not recorded' : durationWords(seconds)}
      </div>
      <div className="bl-report-turnaround-label">launch to delivery</div>
      {timeline.launched_at !== null && timeline.delivered_at !== null && (
        <div className="bl-report-turnaround-clock">
          {utcClock(timeline.launched_at)} to {utcClockFrom(timeline.delivered_at, timeline.launched_at)} UTC
        </div>
      )}
    </div>
  );
}

/**
 * The build timeline: one bar per step on a launch-to-delivery scale.
 *
 * Each row's bar spans the step's first stamp to its last; the vertical rule is
 * `delivered`. A step that finished after delivery (tracking certificates land
 * in the finisher sweep) is clamped at the rule and says by how much. Steps the
 * order never did (photos off, no forwarding target) are not rows at all.
 */
function ReportTimeline({ timeline }: { timeline: DeliveryReport['timeline'] }): JSX.Element {
  const start = timeline.launched_at ? Date.parse(timeline.launched_at) : null;
  const finish = timeline.delivered_at ? Date.parse(timeline.delivered_at) : null;
  const scale = start !== null && finish !== null && finish > start ? { start, span: finish - start } : null;
  const rows = timeline.rows.filter(
    (row): row is typeof row & { first: string; last: string } => row.count > 0 && row.first !== null && row.last !== null,
  );

  if (rows.length === 0) {
    return <div className="bl-hint">No step stamps on this order.</div>;
  }

  return (
    <div className="bl-report-rows">
      {scale !== null && timeline.launched_at !== null && timeline.delivered_at !== null && (
        <div className="bl-report-axis" aria-hidden="true">
          <span />
          <span className="bl-report-axis-scale">
            <span>launched {utcClock(timeline.launched_at, true)}</span>
            <span>delivered {utcClock(timeline.delivered_at, true)}</span>
          </span>
          <span />
        </div>
      )}
      {rows.map((row) => {
        const firstMs = Date.parse(row.first);
        const lastMs = Date.parse(row.last);
        const pct = (ms: number): number =>
          scale === null ? 0 : Math.min(100, Math.max(0, ((ms - scale.start) / scale.span) * 100));
        const left = pct(firstMs);
        const width = Math.max(0, pct(lastMs) - left);
        const after = scale !== null && lastMs > scale.start + scale.span
          ? offsetWords(lastMs - (scale.start + scale.span))
          : null;
        const title =
          `${row.label}: ${utcClock(row.first, true)} to ${utcClock(row.last, true)} UTC`
          + (scale !== null
            ? ` (${offsetWords(firstMs - scale.start)} to ${offsetWords(lastMs - scale.start)} after launch)`
            : '')
          + `, ${plural(row.count, 'item', 'items')}`
          + (after !== null ? `, finished ${after} after delivery` : '');
        return (
          <div key={row.step} className="bl-report-row" tabIndex={0} title={title}>
            <span className="bl-report-row-label">{row.label}</span>
            <span className="bl-report-track" aria-hidden="true">
              {scale !== null && (
                <span
                  className="bl-report-bar"
                  style={{ left: `min(${left}%, calc(100% - 3px))`, width: `max(${width}%, 3px)` }}
                />
              )}
              {scale !== null && <span className="bl-report-rule" />}
            </span>
            <span className="bl-report-row-times">
              <span>
                {utcClock(row.first, true)} to {utcClock(row.last, true)}
              </span>
              <span className="bl-report-row-count">{row.count}</span>
              {after !== null && <span className="bl-report-row-after">after delivery ({after})</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** One recorded finding, or (`note`) one informational note: the same row,
 *  with the code in the neutral colour because nothing about it held delivery. */
function ReportFindingRow({ report, finding, note = false }: {
  report: DeliveryReport;
  finding: DeliveryReport['issues'][number];
  note?: boolean;
}): JSX.Element {
  return (
    <div className={`bl-report-issue ${note ? 'bl-report-issue--note' : ''}`}>
      <span className="bl-report-issue-stack">
        {finding.stack === 'sequencer'
          ? PROVIDER_LABEL[report.stacks.sequencer.provider] ?? 'Sequencer'
          : finding.stack === 'google' ? 'Google' : finding.stack === 'imaps' ? 'IMAPs' : 'MasterInbox'}
      </span>
      <span className="bl-report-issue-code">{finding.code}</span>
      <span className="bl-report-issue-count">{plural(finding.count, 'item', 'items')}</span>
      {finding.sample.length > 0 && (
        <details className="bl-details">
          <summary>Show {plural(finding.sample.length, 'sample', 'samples')}</summary>
          <div className="bl-details-body">
            {finding.sample.map((s, i) => (
              <div key={`${i}:${s}`} className="bl-mono">{s}</div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

/**
 * The delivery report, and the first thing on a delivered order's page.
 *
 * The hard gates further down are a checklist over our own stamps; this is the
 * oracles' word on what Google, the IMAPs orchestrator, the sequencer and
 * MasterInbox actually hold, read live after delivery. Three states: not run
 * yet (null), the station itself failed ({ error }), or the report - verdict,
 * one sentence, the turnaround stat, four tiles, the build timeline, and the
 * issues only when there are any.
 *
 * PURE: props in, markup out, no data fetching and nothing from buildApi at
 * render time, so the static preview (Part C of the redesign brief) renders it
 * with react-dom/server from a fixture.
 */
export function DeliveryReportHero({
  report,
  explanation,
  order,
  onRerun,
  busy,
}: {
  report: DeliveryReport | { error: string } | null;
  explanation?: DeliveryExplanation;
  order: Pick<BuildOrderRow, 'label' | 'status' | 'delivered_at' | 'closed_at'> & { overrides?: Record<string, unknown> };
  onRerun: () => void;
  busy: boolean;
}): JSX.Element {
  const showExplanation = !isDelivered(order.status);
  const rerun = (
    <button
      type="button"
      className="bl-btn bl-btn--secondary bl-btn--small"
      disabled={busy}
      title="Re-read available stacks and save a new report. Provider checks need the borrowed API keys."
      onClick={onRerun}
    >
      Re-run QA
    </button>
  );

  if (report === null || 'error' in report) {
    const failed = report !== null;
    return (
      <section className={`bl-report bl-report--${failed ? 'rose' : 'muted'}`} aria-label={`Delivery report for ${order.label}`}>
        <div className="bl-report-head">
          <div className="bl-report-lead">
            <h2 className="bl-report-verdict">{failed ? 'Delivery checks need attention' : 'Delivery checks pending'}</h2>
            {(!explanation || !showExplanation) && <p className="bl-report-sentence">
              {failed
                ? `${report.error}. Verification has not completed.`
                : order.overrides?.build_version === 2
                  ? 'Every required stack must pass verification before this order is delivered.'
                  : 'It runs on its own when an order is delivered. Re-run QA asks for one now.'}
            </p>}
          </div>
          {rerun}
        </div>
        {showExplanation && explanation && <DeliveryMeaning explanation={explanation} />}
      </section>
    );
  }

  const { verdict, tone, sentence } = headlineOf(report, order);
  const tiles = tilesOf(report, order.status);
  const notes = report.notes ?? [];
  const historical = hasHistoricalQaGap(report, order.status);
  const qaTone = report.issues.length > 0 || report.verdict === 'issues' ? 'err'
    : report.verdict === 'all_clear' ? 'ok' : historical ? 'muted' : 'warn';
  const reportTone = explanation ? explanation.kind === 'action' ? 'rose'
    : explanation.kind === 'verification' ? 'honey' : 'sage' : tone;

  return (
    <section className={`bl-report bl-report--${reportTone}`} aria-label={`Delivery report for ${order.label}`}>
      <div className="bl-report-head">
        <div className="bl-report-lead">
          <h2 className="bl-report-verdict">{verdict}</h2>
          {!explanation && <span className={`bl-badge bl-badge--${qaTone} bl-report-qa`}>
            {qaTone === 'err' ? 'QA found issues' : report.verdict === 'all_clear' ? 'Verified at the saved QA check'
              : historical ? 'Historical QA coverage limited' : 'QA incomplete — review required'}
          </span>}
          <p className="bl-report-sentence">{explanation
            ? `${report.totals.mailboxes} mailboxes · ${report.totals.domains} domains · Saved check ${fmtDate(report.generated_at)}`
            : sentence}</p>
        </div>
      </div>

      {showExplanation && explanation && <DeliveryMeaning explanation={explanation} />}

      {explanation && explanation.issues.some((issue) => issue.kind === 'action' || issue.kind === 'verification') && (
        <div className="bl-report-section">
          <h3 className="bl-report-subtitle">What needs attention</h3>
          {explanation.issues.filter((issue) => issue.kind === 'action' || issue.kind === 'verification').map((issue) => (
            <IssueMeaning key={`${issue.stack}:${issue.code}`} issue={issue} />
          ))}
        </div>
      )}

      {explanation && explanation.issues.some((issue) => issue.kind === 'note') && (
        <div className="bl-report-section">
          <h3 className="bl-report-subtitle">Notes (did not hold delivery)</h3>
          {explanation.issues.filter((issue) => issue.kind === 'note').map((issue) => (
            <IssueMeaning key={`${issue.stack}:${issue.code}`} issue={issue} />
          ))}
        </div>
      )}

      <details className="bl-report-disclosure" open={isDelivered(order.status) || (!explanation && report.verdict !== 'all_clear')}>
        <summary>Verification details and build timeline</summary>
        <div className="bl-report-evidence">
        <div className="bl-report-head"><ReportTurnaround timeline={report.timeline} />{rerun}</div>

      <div className="bl-report-tiles">
        {tiles.map((tile) => (
          <div key={tile.name} className={`bl-report-tile ${tile.unproven ? 'bl-report-tile--unproven' : ''} ${tile.historical ? 'bl-report-tile--historical' : ''}`}>
            <div className="bl-report-tile-head">
              <div className="bl-report-tile-name">{tile.name}</div>
              {tile.workspace !== null && (
                <div className="bl-report-tile-workspace" title={tile.workspace}>{tile.workspace}</div>
              )}
            </div>
            {tile.mark !== null && (
              <span className={`bl-report-tile-mark bl-report-tile-mark--${tile.mark}`} aria-hidden="true">
                {TILE_MARK[tile.mark]}
              </span>
            )}
            <div className={`bl-report-tile-metric ${tile.word ? 'bl-report-tile-metric--word' : ''}`}>
              {tile.metric}
            </div>
            {tile.label !== null && <div className="bl-report-tile-label">{tile.label}</div>}
            {tile.subs.length > 0 && (
              <ul className="bl-report-tile-subs">
                {tile.subs.map((sub) => (
                  <li key={sub}>{sub}</li>
                ))}
              </ul>
            )}
            {tile.note !== null && <div className="bl-report-tile-note">{tile.note}</div>}
          </div>
        ))}
      </div>

      <div className="bl-report-section">
        <h3 className="bl-report-subtitle">Build timeline</h3>
        <ReportTimeline timeline={report.timeline} />
      </div>

      {report.issues.length > 0 && (
        <div className="bl-report-section">
          <h3 className="bl-report-subtitle">Recorded findings</h3>
          <div className="bl-report-issues">
            {report.issues.map((issue) => (
              <ReportFindingRow key={`${issue.stack}:${issue.code}`} report={report} finding={issue} />
            ))}
          </div>
        </div>
      )}

      {notes.length > 0 && (
        <div className="bl-report-section">
          <h3 className="bl-report-subtitle">Notes</h3>
          <div className="bl-report-issues">
            {notes.map((item) => (
              <ReportFindingRow key={`${item.stack}:${item.code}`} report={report} finding={item} note />
            ))}
          </div>
        </div>
      )}
        </div>
      </details>
    </section>
  );
}

/**
 * How long this station has been stamping, first stamp to last.
 *
 * A SPAN, NOT A DURATION OF WORK, and the label says so: the stations overlap by
 * design (rename runs while DKIM is still minting, a panel's IMAPs push goes the
 * moment its renames land), so this measures wall-clock from the first item this
 * station finished to the most recent one - which is the number an operator
 * comparing "which station is dragging" actually wants. A station still running
 * grows it every lap.
 */
function stationSpan(
  station: BuildStationCard,
  stamps: Record<string, BuildStampWindow>,
): { text: string; count: number } | null {
  const owned = (STATION_STAMPS[station.name] ?? [])
    .map((name) => stamps[name])
    .filter((w): w is BuildStampWindow => w !== undefined);
  if (owned.length === 0) return null;
  let first = owned[0].first;
  let last = owned[0].last;
  let count = 0;
  for (const w of owned) {
    if (w.first < first) first = w.first;
    if (w.last > last) last = w.last;
    count += w.count;
  }
  return { text: elapsed(first, new Date(last).getTime()), count };
}

function StationCard({
  station,
  parked,
  span,
}: {
  station: BuildStationCard;
  parked: number;
  span: { text: string; count: number } | null;
}): JSX.Element {
  const meta = STATION_LABEL[station.name];
  const complete = station.total > 0 && station.done >= station.total;
  return (
    <div
      className={`bl-station ${complete ? 'bl-station--done' : station.ready > 0 ? 'bl-station--live' : ''}`}
      title={meta ? meta.what : station.name}
    >
      <div className="bl-station-name">{meta ? meta.label : station.name}</div>
      <Progress done={station.done} total={station.total} />
      <div className="bl-station-counts">
        <span className="bl-station-count" title="work the coordinator can dispatch right now">
          <span className="bl-num">{station.ready}</span> ready
        </span>
        <span className="bl-station-count" title="not startable yet: upstream stamps missing, or parked">
          <span className="bl-num">{station.waiting}</span> waiting
        </span>
        {parked > 0 && (
          <span
            className="bl-badge bl-badge--err"
            title={
              "up to this many of the waiting items are parked, so this station's gate is "
              + 'skipping them. The count is capped at waiting: an item this station already '
              + 'finished counts as done however it failed later. Retry failed or Continue anyway '
              + 'is what releases them.'
            }
          >
            {parked} parked
          </span>
        )}
      </div>
      <div className="bl-station-scope">
        <span>
          {station.scope} / {station.queue} queue
        </span>
        {span !== null && (
          <span
            className="bl-station-span"
            title={
              `first stamp to last across ${span.count} stamp(s) on this order. Wall clock, not `
              + 'time spent working: the stations overlap by design.'
            }
          >
            Span {span.text}
          </span>
        )}
      </div>
    </div>
  );
}

export function OrderDetail({
  orderId,
  onBack,
  showToast,
}: {
  orderId: number;
  onBack: () => void;
  showToast: BuildShowToast;
}): JSX.Element {
  const [detail, setDetail] = useState<BuildOrderDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<OrderAction | null>(null);
  const [confirming, setConfirming] = useState<'continue' | 'abort' | null>(null);
  const [abortTyped, setAbortTyped] = useState('');
  const [abortReason, setAbortReason] = useState('');
  const [forceOpen, setForceOpen] = useState(false);
  const [forceCodes, setForceCodes] = useState<string[]>([]);
  const [releaseHours, setReleaseHours] = useState(24);
  const [tab, setTab] = useState<ItemTab>('domains');
  const [codeFilter, setCodeFilter] = useState('');

  /** Monotonic request id: the reload fired straight after an action is the one
   *  that must win, and a 3 s poll is often already in the air when it goes. */
  const reqId = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++reqId.current;
    try {
      const fresh = await buildApi.getOrder(orderId);
      if (mine !== reqId.current) return;
      setDetail(fresh);
      setLoadError(null);
    } catch (err) {
      if (mine !== reqId.current) return;
      setLoadError(buildErrorMessage(err));
    }
  }, [orderId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Poll while the order can still move - and while the first read has not
   * landed at all, so a transient 500 on open leaves a screen that fills itself
   * in rather than a dead one whose only button goes back.
   */
  const live = detail === null || !TERMINAL.has(detail.order.status);

  const tick = useCallback(() => {
    void load();
  }, [load]);
  useVisiblePoll(tick, POLL_MS, live);

  async function run(action: OrderAction): Promise<void> {
    setBusy(action);
    setConfirming(null);
    setAbortTyped('');
    try {
      if (action === 'retry') {
        const { cleared } = await buildApi.retryFailed(orderId, forceCodes);
        setForceCodes([]);
        showToast(
          cleared > 0 ? 'success' : 'warn',
          cleared > 0
            ? `${cleared} item(s) put back. The coordinator picks them up on its next lap.`
            : 'Nothing was cleared. Either no item carries a transient error, or the ones that are '
              + 'parked carry permanent codes - name those in force retry once you have fixed what '
              + 'caused them.',
        );
      } else if (action === 'continue') {
        const { abandoned } = await buildApi.continueAnyway(orderId);
        showToast(
          'warn',
          `Abandoned ${abandoned.domains} domain(s) and ${abandoned.mailboxes} mailbox(es). `
            + 'The order is delivered without them.',
        );
      } else if (action === 'release') {
        const { release_retry_until: until, held } = await buildApi.armReleaseRetry(orderId, releaseHours);
        showToast('success', `Retrying ${held} held domain(s) every hour until ${fmtDate(until)}.`);
      } else if (action === 'release-stop') {
        await buildApi.stopReleaseRetry(orderId);
        showToast('warn', 'Hourly retry stopped. The held domains stay parked.');
      } else if (action === 'qa') {
        await buildApi.rerunDeliveryReport(orderId);
        // A closed order stops polling (TERMINAL), so the replacement report
        // will not walk in on its own there; say so instead of leaving the
        // operator staring at the old one.
        showToast(
          'success',
          live
            ? 'QA re-run queued. The Delivery report card refreshes itself when the run lands.'
            : 'QA re-run queued. This order is closed, so the page no longer polls - press Refresh '
              + 'in a minute to see the new report.',
        );
      } else {
        await buildApi.abortOrder(orderId, abortReason);
        setAbortReason('');
        showToast('warn', 'Order aborted. Borrowed credentials purged; nothing built was torn down.');
      }
      await load();
    } catch (err) {
      showToast('error', buildErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  if (!detail) {
    return (
      <>
        <div className="bl-detailhead">
          <button type="button" className="bl-btn bl-btn--secondary bl-btn--small" onClick={onBack}>
            Back to orders
          </button>
          {loadError !== null && (
            <button
              type="button"
              className="bl-btn bl-btn--ghost bl-btn--small"
              onClick={() => void load()}
            >
              Retry
            </button>
          )}
        </div>
        <section className="bl-card">
          <div className="bl-empty">
            {loadError === null
              ? 'Loading order...'
              : `Could not load order ${orderId}: ${loadError}. Retrying every 3 seconds - Retry asks now.`}
          </div>
        </section>
      </>
    );
  }

  const {
    order, profile, panels, domains, mailboxes, board, hard_gates: gates, finishers, timings,
  } = detail;
  const browserJobs = detail.browser_jobs ?? [];
  const usesCollector = profile?.records?.dmarc_policy?.includes('<collector>') ?? false;
  const parkedRows = detail.attention.filter((row) => row.parked);
  const softRows = detail.attention.filter((row) => !row.parked);

  /** The codes the force-retry picker offers: everything currently parked on
   *  THIS order. Permanent ones are labelled, not filtered - see
   *  BUILD_PERMANENT_CODES. A transient code named here is a harmless no-op
   *  (the default pass clears it anyway). */
  const parkedCodes = [...new Set(parkedRows.map((row) => row.error_code))].sort();

  /**
   * Parked items this station's gate is skipping - CLAMPED TO ITS WAITING COUNT.
   *
   * The raw count is order-wide for the scope, and that over-reports: a mailbox
   * parked at the sequencer was renamed hours ago, so the rename station has it
   * in `done`, not in `waiting`, and an unclamped badge would put a red "3
   * parked" on a station that finished cleanly. The clamp makes the badge an
   * upper bound on a number that is definitionally inside `waiting`, and it
   * falls to zero - the badge disappears - the moment a station has nothing
   * waiting at all, which is the case the raw count got flatly wrong.
   */
  const parkedFor = (station: BuildStationCard): number => {
    const scope = STATION_ITEMS[station.name];
    if (!scope || station.waiting <= 0) return 0;
    let n = 0;
    if (scope.domains) {
      n += domains.filter((d) => d.abandoned_at === null && buildIsParked(d.error_code)).length;
    }
    if (scope.mailboxes) {
      n += mailboxes.filter((m) => m.abandoned_at === null && buildIsParked(m.error_code)).length;
    }
    if (scope.panels) n += panels.filter((p) => buildIsParked(p.error_code)).length;
    return Math.min(n, station.waiting);
  };

  // The engine's own fitness rules (ACTION_FITNESS in src/routes/build-admin.ts),
  // mirrored so a button explains itself instead of answering 409. Unfit actions
  // are DISABLED rather than hidden: an operator hunting for a Retry button that
  // is not on the page learns nothing, while a greyed one with the reason in its
  // tooltip answers the question. The server still has the last word.
  const parkedOrder = order.status === 'needs_attention' || order.status === 'awaiting_capacity';
  // Retry failed also fits a RUNNING order with parked items: a station keeps
  // working while one panel's items sit on a transient code, and the route
  // admits `running` for exactly that. Continue anyway does not - it needs the
  // order itself to have stopped.
  const retryFit = parkedOrder || (order.status === 'running' && parkedRows.length > 0);
  const terminal = TERMINAL.has(order.status);
  const actionBlocked = busy !== null;

  const endedAt = order.delivered_at ?? order.closed_at
    ?? (order.status === 'aborted' ? order.secrets_purged_at ?? order.updated_at : null);
  // Panels carry codes of their own (a browser job that failed for the whole
  // tenant), so they belong in the option list and under the filter like the
  // other two tables.
  const codes = [...new Set(
    [
      ...domains.map((d) => d.error_code),
      ...mailboxes.map((m) => m.error_code),
      ...panels.map((p) => p.error_code),
    ].filter((c): c is string => c !== null),
  )].sort();

  /**
   * The filter names a code no item carries any more.
   *
   * The value is KEPT rather than reset, and the select grows an explicit option
   * saying so. A retry clears codes three seconds after the operator pressed it,
   * and silently snapping the filter back to "All items" would drop them into an
   * unexplained 600-row table; keeping it - with an empty table that says the
   * code is gone - is the confirmation that the retry worked.
   */
  const filterCleared =
    codeFilter !== '' && codeFilter !== '__errors' && !codes.includes(codeFilter);

  const matches = (code: string | null): boolean => {
    if (codeFilter === '') return true;
    if (codeFilter === '__errors') return code !== null;
    return code === codeFilter;
  };
  const shownDomains = domains.filter((d) => matches(d.error_code));
  const shownMailboxes = mailboxes.filter((m) => matches(m.error_code));
  const shownPanels = panels.filter((p) => matches(p.error_code));

  const noMatch = filterCleared
    ? `No item carries ${codeFilter} any more - it was cleared since you set this filter, by a `
      + 'retry or by an abandon.'
    : 'Nothing matches that filter.';

  // Completed orders lead with navigation, then the open verification report.
  // During v2 delivery verification the report still leads with missing proof.
  const delivered = isDelivered(order.status);
  const deliveryReport = (delivered || (order.overrides.build_version === 2 && order.phase === 'deliver')) ? (
    <DeliveryReportHero
      report={detail.delivery_report}
      explanation={detail.delivery_explanation}
      order={order}
      onRerun={() => void run('qa')}
      busy={busy !== null}
    />
  ) : null;

  return (
    <>
      {!delivered && deliveryReport}

      <div className="bl-detailhead">
        <button type="button" className="bl-btn bl-btn--secondary bl-btn--small" onClick={onBack}>
          Back to orders
        </button>
        <h2 className="bl-detailtitle">{order.label}</h2>
        <span className="bl-cell-faint">#{order.id}</span>
        <span className={`bl-badge bl-badge--${buildStatusTone(order.status)}`}>
          {BUILD_STATUS_LABEL[order.status]}
        </span>
        <span className={`bl-badge bl-badge--${buildPhaseTone(order.phase, order.status)}`}>{PHASE_LABEL[order.phase] ?? order.phase}</span>
        {(delivered || terminal) && <button type="button" className="bl-btn bl-btn--ghost bl-btn--small"
          onClick={() => void load()}>Refresh</button>}
        {live && <span className="bl-pulse" title="Polling every 3 seconds while this tab is visible" />}
        <div className="bl-actions-spacer" />
        <ElapsedClock from={order.launched_at ?? order.created_at} to={endedAt} live={live}
          label={order.launched_at
            ? delivered ? 'Launch to delivery' : order.status === 'aborted' ? 'Launch to abort' : 'Since launch'
            : delivered ? 'Created to delivery' : order.status === 'aborted' ? 'Created to abort' : 'Since creation'} />
      </div>

      {delivered && deliveryReport}

      {detail.delivery_progress && (
        <section className="bl-card" aria-label="Delivery verification progress">
          <div className="bl-card-header"><h2 className="bl-card-title">Delivery verification</h2>
            <ElapsedClock from={detail.delivery_progress.started_at} to={detail.delivery_progress.finished_at} live={!terminal} label="Verification elapsed" />
          </div>
          <div className="bl-meta">{detail.delivery_progress.checks.map((check) => (
            <div key={check.kind}>
              <MetaItem label={({ seats: 'Google seats', domains: 'Google domains', dns: 'DNS records', logins: 'Mailbox logins', sequencer: 'Sequencer settings and warmup', masterinbox: 'MasterInbox' } as Record<string, string>)[check.kind] ?? check.kind}
                value={({ queued: 'Waiting to start', running: 'Checking — awaiting completion', passed: 'Passed', failed: 'Check failed', read_error: 'Could not read provider', incomplete: 'No completion recorded' })[check.state]} />
              {check.started_at && <ElapsedClock from={check.started_at} to={check.finished_at ?? detail.delivery_progress?.finished_at ?? null} live={!terminal} label="Check elapsed" />}
            </div>
          ))}</div>
        </section>
      )}

      {detail.performance && (
        <details className="bl-card bl-collapsible" aria-label="Order speed baseline" open={!delivered && !terminal}>
          <summary className="bl-card-header">
            <h2 className="bl-card-title">Order speed baseline</h2>
            <a className="bl-btn bl-btn--secondary bl-btn--small" href={`/api/build/orders/${order.id}/baseline`} download>
              Download baseline
            </a>
          </summary>
          <div className="bl-meta">
            <MetaItem label={detail.performance.execution_version < 2 ? 'Launch to delivery' : 'Launch to verification'} value={detail.performance.elapsed_seconds === null ? 'Not launched' : `${(detail.performance.elapsed_seconds / 60).toFixed(1)} min`} />
            <MetaItem label="Target" value="30 min" />
            <MetaItem label="Result" value={detail.performance.execution_version < 2 ? 'Historical timing' : detail.performance.target_met === true ? 'Full roster verified within target' : detail.performance.target_met === false ? 'Full roster verified; target exceeded' : 'Awaiting full roster verification'} />
            <MetaItem label="Recorded failures" value={String(detail.performance.failure_events)} />
            <MetaItem label="Deferred jobs" value={String(detail.performance.deferral_events)} />
          </div>
          {detail.performance.waits.length > 0 && (
            <details className="bl-details">
              <summary>View recorded waits and failures</summary>
              <div className="bl-table-wrap"><table className="bl-table">
                <thead><tr><th>Station</th><th>Reason</th><th>Events</th><th>Scheduled wait</th></tr></thead>
                <tbody>{detail.performance.waits.map((wait) => (
                  <tr key={`${wait.station}:${wait.event}:${wait.code}`}>
                    <td>{STATION_LABEL[wait.station]?.label ?? wait.station}</td>
                    <td>{wait.code ?? wait.event}</td><td>{wait.count}</td>
                    <td>{(wait.scheduled_wait_seconds / 60).toFixed(1)} min</td>
                  </tr>
                ))}</tbody>
              </table></div>
              <p className="bl-hint">Waits can overlap across jobs. The download includes settings, timestamps, workers and attempt history.</p>
            </details>
          )}
        </details>
      )}

      {order.status === 'aborted' && (
        <div className="bl-notice bl-notice--warn" role="status">
          <strong>Aborted</strong>
          {detail.abort
            ? ` by ${detail.abort.operator ?? 'an operator'} at ${fmtDate(detail.abort.occurred_at)}`
              + (detail.abort.reason ? `: ${detail.abort.reason}` : ' - no reason was recorded.')
            : ' - no reason was recorded (aborted before reasons were kept).'}
        </div>
      )}

      <div className="bl-meta">
        <MetaItem
          label="Profile"
          value={profile ? `${profile.name} (${profile.customer})` : `profile ${order.profile_id}`}
        />
        <MetaItem label="Created" value={fmtDate(order.created_at)} />
        {profile && <MetaItem label="Panel split" value={panelSplitLabel(profile)} />}
        <MetaItem
          label="Delivered"
          value={order.delivered_at ? fmtDate(order.delivered_at) : 'not yet'}
        />
        <MetaItem label="Closed" value={order.closed_at ? fmtDate(order.closed_at) : 'not yet'} />
        <MetaItem
          label={delivered || terminal ? 'Final phase entered' : 'Phase started'}
          value={
            order.phase_started_at
              ? fmtDate(order.phase_started_at)
              : 'not stamped'
          }
        />
        <MetaItem label="Size" value={`${domains.length} domains, ${mailboxes.length} mailboxes, ${panels.length} panels`} />
        <MetaItem
          label="Sequencer"
          value={
            order.sequencer_skipped_at
              ? 'skipped (provider none)'
              : order.sequencer_run_id
                ? `run ${order.sequencer_run_id}`
                : 'no run yet'
          }
        />
        <MetaItem label="Forwarding" value={order.forwarding_url ?? 'none'} />
        <MetaItem
          label="Borrowed credentials"
          value={
            order.secrets_purged_at
              ? `purged ${fmtDate(order.secrets_purged_at)}`
              : [
                  order.has_registrar_creds ? 'registrar' : null,
                  order.has_sequencer_api_key ? 'sequencer' : null,
                  order.has_masterinbox_api_key ? 'masterinbox' : null,
                ]
                  .filter((s): s is string => s !== null)
                  .join(', ') || 'none held'
          }
        />
      </div>

      {loadError !== null && (
        <div className="bl-notice bl-notice--err bl-notice--standalone">
          <strong>The last refresh failed.</strong> {loadError}. What is on screen is the last good
          read.
        </div>
      )}

      {order.status === 'awaiting_capacity' && (
        <div className="bl-notice bl-notice--warn bl-notice--standalone">
          <strong>Waiting for panel capacity - self-healing.</strong> The coordinator re-plans this
          order automatically every 10 minutes, and <code>out_of_licences</code> items clear
          themselves when seats free up. No button press needed; what actually unblocks it is
          seats appearing in the estate (a batch finishing, a panel transformed, new panels
          arriving). Escalate only if it sits here for hours.
        </div>
      )}

      {parkedRows.length > 0 && (
        <section className="bl-card">
          <div className="bl-card-header">
            <h2 className="bl-card-title">
              PI action needed
              <span className="bl-badge bl-badge--err bl-badge--inline">{parkedRows.length}</span>
            </h2>
            <span className="bl-hint">
              Resolve the cause below before retrying. A retry alone cannot fix missing access or a domain ownership conflict.
            </span>
          </div>
          <div className="bl-table-wrap bl-table-wrap--short">
            <table className="bl-table">
              <thead>
                <tr>
                  <th className="bl-col-center">Kind</th>
                  <th>Affected items</th>
                  <th>Issue, impact and next step</th>
                  <th className="bl-col-center">Failed</th>
                </tr>
              </thead>
              <tbody>
                {groupAttention(parkedRows).map(({ key, row, items }) => (
                  <tr key={key}>
                    <td className="bl-col-center bl-cell-faint">{row.kind}</td>
                    <td>
                      <span>{items.length} affected {items.length === 1 ? 'item' : 'items'}</span>
                      <details className="bl-details"><summary>View affected items</summary>
                        <div className="bl-details-body bl-mono">{items.map((item) => <div key={item}>{item}</div>)}</div>
                      </details>
                    </td>
                    <td><AttentionMeaning explanation={row.explanation} code={row.error_code} /></td>
                    <td className="bl-col-center bl-cell-faint" title={fmtDate(row.error_at)}>
                      {ago(row.error_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!delivered && !terminal && <section className="bl-card">
        <div className="bl-card-header">
          <h2 className="bl-card-title">Actions</h2>
          <div className="bl-actions">
            <button
              type="button"
              className="bl-btn bl-btn--ghost bl-btn--small"
              onClick={() => void load()}
            >
              Refresh
            </button>
          </div>
        </div>
        <div className="bl-card-body">
          <div className="bl-actions">
            <button
              type="button"
              className="bl-btn bl-btn--primary"
              disabled={actionBlocked || !retryFit}
              title={
                retryFit
                  ? 'Clear the transient errors and let the coordinator try them again'
                  : order.status === 'running'
                    ? 'Nothing is parked on this order; the coordinator is still working'
                    : `Retry failed needs a parked order; this one is ${BUILD_STATUS_LABEL[order.status]}`
              }
              onClick={() => void run('retry')}
            >
              {busy === 'retry' ? 'Working...' : 'Retry failed'}
            </button>
            <button
              type="button"
              className="bl-btn bl-btn--secondary"
              disabled={actionBlocked || !parkedOrder}
              title={
                parkedOrder
                  ? 'Abandon the parked items and deliver the rest'
                  : `Continue anyway needs a parked order; this one is ${BUILD_STATUS_LABEL[order.status]}`
              }
              onClick={() => setConfirming('continue')}
            >
              {busy === 'continue' ? 'Working...' : 'Continue anyway'}
            </button>
            <button
              type="button"
              className="bl-btn bl-btn--danger"
              disabled={actionBlocked || terminal}
              title={terminal ? `This order is already ${order.status}` : 'End the order here'}
              onClick={() => {
                setAbortTyped('');
                setAbortReason('');
                setConfirming('abort');
              }}
            >
              {busy === 'abort' ? 'Working...' : 'Abort'}
            </button>
            <div className="bl-actions-spacer" />
            <button
              type="button"
              className="bl-btn bl-btn--ghost bl-btn--small"
              disabled={parkedCodes.length === 0}
              title={
                parkedCodes.length === 0
                  ? 'Nothing is parked on this order'
                  : 'Name the settled refusals a plain retry will not clear'
              }
              onClick={() => setForceOpen((open) => !open)}
            >
              {forceOpen ? 'Hide force retry' : 'Force retry codes'}
              {forceCodes.length > 0 ? ` (${forceCodes.length})` : ''}
            </button>
          </div>

          {forceOpen && (
            <div className="bl-forceretry">
              <p className="bl-hint">
                <strong>Force retry.</strong> A plain Retry failed clears TRANSIENT errors only,
                because a permanent code is an answer Google, Instantly or MasterInbox already gave
                and a retry burns attempts on a settled question. Tick a code here and the next
                Retry failed clears it too. Do that only when you have changed the world outside the
                pipeline - freed the domain from the tenant holding it, rotated the prefix past the
                abuse flag, re-supplied the images or the key - because ticking a code is you
                asserting that you fixed it, not hoping.
              </p>
              <div className="bl-chips">
                {parkedCodes.map((code) => {
                  const on = forceCodes.includes(code);
                  const permanent = BUILD_PERMANENT_CODES.has(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      className={`bl-chip ${on ? 'bl-chip--on' : ''}`}
                      onClick={() =>
                        setForceCodes((prev) =>
                          prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
                        )
                      }
                    >
                      <span className="bl-mono">{code}</span>
                      <span className="bl-cell-faint">{permanent ? 'permanent' : 'transient'}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {detail.release_retry.until !== null ? (
            <div className="bl-forceretry">
              <p className="bl-hint">
                {detail.release_retry.held > 0
                  ? `Retrying ${detail.release_retry.held} held domain(s) every hour until `
                    + `${fmtDate(detail.release_retry.until)}. Last retry `
                    + `${detail.release_retry.last_at ? fmtDate(detail.release_retry.last_at) : 'not yet'}.`
                  : `Hourly retry armed until ${fmtDate(detail.release_retry.until)}. Nothing is held `
                    + 'any more; it stops itself on the next check.'}
              </p>
              <div className="bl-actions">
                <button
                  type="button"
                  className="bl-btn bl-btn--ghost bl-btn--small"
                  disabled={actionBlocked}
                  title="Stop the hourly retry. The held domains stay parked."
                  onClick={() => void run('release-stop')}
                >
                  {busy === 'release-stop' ? 'Working...' : 'Stop'}
                </button>
              </div>
            </div>
          ) : detail.release_retry.held > 0 && (
            <div className="bl-forceretry">
              <p className="bl-hint">
                <strong>{detail.release_retry.held} domain(s) held by another Workspace.</strong> Google
                refused them as already in use, so a plain Retry failed leaves them parked. If the
                previous provider is releasing them, the system can retry them every hour and stop by
                itself once they go through.
              </p>
              <div className="bl-actions">
                <button
                  type="button"
                  className="bl-btn bl-btn--secondary bl-btn--small"
                  disabled={actionBlocked}
                  title="Retry the held domains once an hour, for the hours given, then stop"
                  onClick={() => void run('release')}
                >
                  {busy === 'release' ? 'Working...' : 'Retry hourly until released'}
                </button>
                <label className="bl-hint" htmlFor="bl-release-hours">for</label>
                <input
                  id="bl-release-hours"
                  type="number"
                  className="bl-input bl-input--num bl-input--filter"
                  min={1}
                  max={72}
                  value={releaseHours}
                  onChange={(e) => setReleaseHours(Math.min(72, Math.max(1, Number(e.target.value) || 1)))}
                />
                <span className="bl-hint">hours (max 72)</span>
              </div>
            </div>
          )}
        </div>
      </section>}

      {!!detail.sequencer_progress?.awaiting_configuration.length && <div className="bl-hint">
        Waiting for configuration checks on {detail.sequencer_progress.awaiting_configuration.length} inbox(es). Warmup follows automatically.
      </div>}
      {!!detail.sequencer_progress?.retry_pending.length && <div className="bl-hint">
        {detail.sequencer_progress.retry_pending.length} inbox connection(s) scheduled for automatic retry.
        Next retry {fmtDate(detail.sequencer_progress.retry_pending.map((r) => r.next_retry_at).sort()[0])}.
      </div>}

      <details className="bl-card bl-collapsible" open={!delivered && !terminal}>
        <summary className="bl-card-header">
          <h2 className="bl-card-title">Station board</h2>
          <span className="bl-hint">
            Counts are the coordinator's own gates over this order's items. Ready is work it can
            dispatch now; waiting is everything else, parked items included. The time on each card
            is wall clock from that station's first stamp on this order to its last, not time spent
            working - the stations overlap by design.
          </span>
        </summary>
        <div className="bl-card-body">
          {board.map((phase) => {
            const complete = phase.name === 'deliver' && delivered;
            const current = phase.current && !delivered && !terminal;
            return (
            <div key={phase.name} className={`bl-phase ${complete ? 'bl-phase--done' : current ? 'bl-phase--current' : ''}`}>
              <div className="bl-phase-head">
                <span className="bl-phase-name">{PHASE_LABEL[phase.name] ?? phase.name}</span>
                {complete && <span className="bl-badge bl-badge--ok">Completed</span>}
                {current && (
                  <span className="bl-badge bl-badge--info">
                    {parkedOrder ? 'Waiting' : 'In progress'}
                    {order.phase_started_at ? ` · started ${fmtDate(order.phase_started_at)}` : ''}
                  </span>
                )}
              </div>
              {phase.stations.length === 0 ? (
                <div className="bl-hint">
                  {complete ? `Delivered ${fmtDate(order.delivered_at ?? order.closed_at)}. Delivery work is complete.`
                    : phase.name === 'deliver' ? 'Delivery requires the hard gates and verification checks below.'
                      : 'This phase has no individual stations.'}
                </div>
              ) : (
                <div className="bl-stations">
                  {phase.stations.map((station) => (
                    <StationCard
                      key={station.name}
                      station={station}
                      parked={parkedFor(station)}
                      span={stationSpan(station, timings.stamps)}
                    />
                  ))}
                </div>
              )}
            </div>
          ); })}
        </div>
      </details>

      <details className="bl-card bl-collapsible" open={!delivered && !terminal}>
        <summary className="bl-card-header">
          <h2 className="bl-card-title">Delivery checklist</h2>
          <span className="bl-hint">
            Completed build steps. Final verification is recorded separately in the delivery report.
          </span>
        </summary>
        <div className="bl-card-body">
          {gates === null ? (
            <div className="bl-hint">The deliver phase reported no checklist.</div>
          ) : gates.pass ? (
            <div className="bl-gate bl-gate--ok">
              <div className="bl-gate-head">
                <span className="bl-gate-mark">{'✓'}</span>
                <span className="bl-gate-name">Build steps complete</span>
              </div>
              <div className="bl-gate-detail">
                {delivered
                  ? `Delivery was recorded ${fmtDate(order.delivered_at ?? order.closed_at)}. See the delivery report for verification evidence.`
                  : 'Build steps are complete. Final verification must finish before handoff.'}
              </div>
            </div>
          ) : (
            <div className="bl-gate bl-gate--bad">
              <div className="bl-gate-head">
                <span className="bl-gate-mark">{'✗'}</span>
                <span className="bl-gate-name">
                  {gates.failures.length} build requirement(s) still incomplete
                </span>
              </div>
              <ul className="bl-gate-problems">
                {gates.failures.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </details>

      <details className="bl-card bl-collapsible" open={!delivered && !terminal}>
        <summary className="bl-card-header">
          <h2 className="bl-card-title">Follow-up checks</h2>
          <span className="bl-hint">
            Tracking certificate checks start during the build and continue automatically after delivery. Photos are set to "Anyone" automatically after delivery.
          </span>
        </summary>
        <div className="bl-card-body">
          {finishers === null ? (
            <div className="bl-hint">
              Certificate checks start after DNS is ready. No follow-up result has been recorded yet.
            </div>
          ) : (
            <>
              <TailRow name="Tracking domains" tail={finishers.tracking} />
              <TailRow name="Forwarding proof" tail={finishers.forwarding} />
              <TailRow name="Photo visibility pass" tail={finishers.visibility} />
              <div className="bl-hint">
                {finishers.closed === true
                  ? 'Automatic follow-up has finished and the order was closed. See the saved results above.'
                  : delivered ? 'Delivery is recorded. Follow-up checks are still in progress; see each result above.' : 'Certificate checks run alongside the build; see the next check time above.'}
              </div>
            </>
          )}
          {softRows.length > 0 && (
            <details className="bl-details">
              <summary>Saved follow-up notes ({softRows.length} affected items)</summary>
              <div className="bl-details-body">
                {groupAttention(softRows).map(({ key, row, items }) => (
                  <div className="bl-meaning-issue" key={key}>
                    <AttentionMeaning explanation={row.explanation} code={row.error_code} />
                    <details className="bl-details">
                      <summary>View {items.length} affected {items.length === 1 ? 'item' : 'items'}</summary>
                      <div className="bl-details-body bl-mono">{items.map((item) => <div key={item}>{item}</div>)}</div>
                    </details>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </details>

      {detail.preflight !== null && (
        <section className="bl-card">
          <div className="bl-card-header">
            <h2 className="bl-card-title">Preflight</h2>
            <span className="bl-hint">
              The dry run this order launched on. Re-run it from the Enroll wizard.
            </span>
          </div>
          <div className="bl-card-body">
            <div className={`bl-verdict ${detail.preflight.pass ? 'bl-verdict--ok' : 'bl-verdict--bad'}`}>
              {detail.preflight.pass ? 'PASS' : 'FAIL'}
              <span className="bl-verdict-sub">
                {detail.preflight.checked_at
                  ? `checked ${fmtDate(detail.preflight.checked_at)}`
                  : 'never completed'}
                {detail.preflight.error ? ` - ${detail.preflight.error}` : ''}
                {detail.preflight.panels
                  ? ` - ${detail.preflight.panels.pool_panels} pool panel(s), `
                    + `${detail.preflight.panels.free_seats} free seat(s) at the time`
                  : ''}
              </span>
            </div>
          </div>
        </section>
      )}

      <div className="bl-subnav bl-subnav--tabs">
        {(
          [
            { id: 'domains' as const, label: `Domains (${domains.length})` },
            { id: 'mailboxes' as const, label: `Mailboxes (${mailboxes.length})` },
            { id: 'panels' as const, label: `Panels (${panels.length})` },
          ]
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            className={`bl-subnav-item ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
        <div className="bl-actions-spacer" />
        <select
          className="bl-input bl-input--filter"
          aria-label="Filter by error code"
          value={codeFilter}
          onChange={(e) => setCodeFilter(e.target.value)}
        >
          <option value="">All items</option>
          <option value="__errors">Errors only</option>
          {codes.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
          {filterCleared && (
            <option value={codeFilter}>{codeFilter} (no longer present)</option>
          )}
        </select>
      </div>

      {tab === 'domains' && (
        <section className="bl-card">
          <div className="bl-card-header">
            <h2 className="bl-card-title">Domains</h2>
            <span className="bl-hint">
              Stamps in order: zone active, records, added, verified, DKIM, forwarding, tracking.
              Hover any of them for the exact time.
            </span>
          </div>
          <div className="bl-table-wrap">
            <table className="bl-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th className="bl-col-center">Shape</th>
                  <th>Panel</th>
                  <th>Zone</th>
                  <th className="bl-col-center">Stamps</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {shownDomains.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="bl-empty">
                      {domains.length === 0
                        ? 'No domains on this order - they are written at enrollment, so an empty table means the order row is incomplete.'
                        : noMatch}
                    </td>
                  </tr>
                ) : (
                  shownDomains.map((d) => (
                    <tr
                      key={d.domain}
                      className={buildIsParked(d.error_code) && d.abandoned_at === null ? 'bl-row--bad' : ''}
                    >
                      <td className="bl-cell-mono">
                        {d.domain}
                        {d.abandoned_at !== null && (
                          <span className="bl-badge bl-badge--muted bl-badge--inline">abandoned</span>
                        )}
                        {usesCollector && d.dmarc_collector && (
                          <div className="bl-cell-faint" title="DMARC reports (rua) go to this mailbox">
                            DMARC rua: {d.dmarc_collector}
                          </div>
                        )}
                      </td>
                      <td className="bl-col-center bl-cell-faint">{d.shape}</td>
                      <td className="bl-cell-mono bl-cell-trunc" title={d.panel_email ?? 'not allocated'}>
                        {d.panel_email ?? '-'}
                      </td>
                      <td className="bl-cell-mono bl-cell-faint" title={d.zone_id ?? 'no zone yet'}>
                        {d.zone_id ? d.zone_id.slice(0, 8) : '-'}
                      </td>
                      <td className="bl-col-center">
                        <span className="bl-stamps">
                          {DOMAIN_STAMPS.map((s) => (
                            <Stamp key={s.key} label={s.label} at={d[s.key] as string | null} />
                          ))}
                        </span>
                      </td>
                      <td className={d.error_code ? 'bl-cell-err bl-cell-mono' : 'bl-cell-faint'}>
                        {d.error_code ?? '-'}
                        {d.error_code && (
                          <span className="bl-cell-faint"> {ago(d.error_at)}</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'mailboxes' && (
        <section className="bl-card">
          <div className="bl-card-header">
            <h2 className="bl-card-title">Mailboxes</h2>
            <span className="bl-hint">
              Stamps in order: claimed, licensed, renamed, photo, IMAPs push, login proof,
              sequencer, warmup, MasterInbox.
            </span>
          </div>
          <div className="bl-table-wrap">
            <table className="bl-table">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Holding</th>
                  <th>Panel</th>
                  <th className="bl-col-center">Tag</th>
                  <th className="bl-col-center">Stamps</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {shownMailboxes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="bl-empty">
                      {mailboxes.length === 0 ? 'No mailboxes on this order.' : noMatch}
                    </td>
                  </tr>
                ) : (
                  shownMailboxes.map((m) => (
                    <tr
                      key={m.final_email}
                      className={buildIsParked(m.error_code) && m.abandoned_at === null ? 'bl-row--bad' : ''}
                    >
                      <td className="bl-cell-mono">
                        {m.final_email}
                        {m.abandoned_at !== null && (
                          <span className="bl-badge bl-badge--muted bl-badge--inline">abandoned</span>
                        )}
                      </td>
                      <td
                        className="bl-cell-mono bl-cell-faint bl-cell-trunc"
                        title={m.holding_email ?? 'no holding address yet'}
                      >
                        {m.holding_email ?? '-'}
                      </td>
                      <td className="bl-cell-mono bl-cell-trunc" title={m.panel_email ?? 'not allocated'}>
                        {m.panel_email ?? '-'}
                      </td>
                      <td className="bl-col-center bl-cell-faint">{m.group_tag ?? '-'}</td>
                      <td className="bl-col-center">
                        <span className="bl-stamps">
                          {MAILBOX_STAMPS.map((s) => (
                            <Stamp key={s.key} label={s.label} at={m[s.key] as string | null} />
                          ))}
                        </span>
                      </td>
                      <td className={m.error_code ? 'bl-cell-err bl-cell-mono' : 'bl-cell-faint'}>
                        {m.error_code ?? '-'}
                        {m.error_code && (
                          <span className="bl-cell-faint"> {ago(m.error_at)}</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'panels' && (
        <section className="bl-card">
          <div className="bl-card-header">
            <h2 className="bl-card-title">Panels</h2>
            <span className="bl-hint">
              The tenants this order was allocated onto. A panel error is a browser job that failed
              for the whole panel, not for one item on it.
            </span>
          </div>
          <div className="bl-table-wrap">
            <table className="bl-table">
              <thead>
                <tr>
                  <th>Panel</th>
                  <th className="bl-col-center">Domains</th>
                  <th className="bl-col-center">Mailboxes</th>
                  <th className="bl-col-center">Claimed</th>
                  <th className="bl-col-center">Licensed</th>
                  <th>Browser job</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {shownPanels.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="bl-empty">
                      {panels.length === 0
                        ? 'No panels yet: allocation has not run, or it could not place this order.'
                        : noMatch}
                    </td>
                  </tr>
                ) : (
                  shownPanels.map((p) => {
                    const job = browserJobs.find((j) => j.panel_email === p.panel_email);
                    return (
                      <tr key={p.panel_email} className={p.error_code ? 'bl-row--bad' : ''}>
                        <td className="bl-cell-mono">{p.panel_email}</td>
                        <td className="bl-col-center bl-num">
                          {domains.filter((d) => d.panel_email === p.panel_email).length}
                        </td>
                        <td className="bl-col-center bl-num">
                          {mailboxes.filter((m) => m.panel_email === p.panel_email).length}
                        </td>
                        <td className="bl-col-center">
                          <Stamp label="claimed" at={p.claimed_at} />
                        </td>
                        <td className="bl-col-center">
                          <Stamp label="licensed" at={p.licensed_at} />
                        </td>
                        <td className="bl-cell-faint">
                          {job
                            ? `${job.state}${
                              job.attempts_made > 0
                                ? ` - attempt ${job.attempts_made + 1}${job.attempts ? ` of ${job.attempts}` : ''}`
                                : ''
                            }`
                            : '-'}
                        </td>
                        <td className={p.error_code ? 'bl-cell-err bl-cell-mono' : 'bl-cell-faint'}>
                          {p.error_code ?? '-'}
                          {p.error_code && <span className="bl-cell-faint"> {ago(p.error_at)}</span>}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {confirming !== null && (
        <div className="bl-modal-overlay">
          <div className="bl-modal">
            <div className="bl-modal-header">
              <h3 className="bl-modal-title">{CONFIRM[confirming].title}</h3>
            </div>
            <div className="bl-modal-body">
              <p>{CONFIRM[confirming].body}</p>
              <p className="bl-cell-faint">
                {order.label} - #{order.id} - {domains.length} domains, {mailboxes.length} mailboxes
                {parkedRows.length > 0 ? `, ${parkedRows.length} parked` : ''}
              </p>
              {confirming === 'abort' && (
                <>
                  {/* Recorded on the order's history. Optional, so a reason the
                      operator cannot put into words never blocks the abort; the
                      next person reading the order is who it is for. */}
                  <div className="bl-field">
                    <label className="bl-field-label" htmlFor="bl-abort-reason">
                      Why is this order being aborted? (recorded on the order)
                    </label>
                    <textarea
                      id="bl-abort-reason"
                      className="bl-textarea"
                      rows={3}
                      maxLength={ABORT_REASON_MAX}
                      value={abortReason}
                      onChange={(e) => setAbortReason(e.target.value)}
                      placeholder="e.g. customer reduced the order; re-enrolling as a smaller one"
                    />
                    <span className="bl-hint">
                      Optional, up to {ABORT_REASON_MAX} characters. No URLs or credentials - it goes
                      into the order history.
                    </span>
                  </div>
                  <div className="bl-field">
                    <label className="bl-field-label" htmlFor="bl-abort-confirm">
                      Type {ABORT_WORD} to confirm
                    </label>
                    <input
                      id="bl-abort-confirm"
                      className="bl-input"
                      autoComplete="off"
                      value={abortTyped}
                      onChange={(e) => setAbortTyped(e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="bl-modal-footer">
              <button
                type="button"
                className="bl-btn bl-btn--danger"
                disabled={
                  actionBlocked || (confirming === 'abort' && abortTyped.trim() !== ABORT_WORD)
                }
                onClick={() => void run(confirming)}
              >
                {busy !== null ? 'Working...' : CONFIRM[confirming].cta}
              </button>
              <button
                type="button"
                className="bl-btn bl-btn--secondary"
                disabled={actionBlocked}
                onClick={() => {
                  setConfirming(null);
                  setAbortTyped('');
                  setAbortReason('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
